import React, { useEffect, useRef, useState } from 'react';
import firebase from '../firebaseConfig';
import { clone } from '../utils/grade';
import {
  stageParamSchema,
  pipelineBrief,
  parseDottedPath,
  getAtPath,
  describeValue,
  valueApplied,
  referenceDeltaLines,
} from '../utils/stageAssist';

const API = process.env.REACT_APP_UPLOAD_API || '';

// Grok reviews its own result, so refinement converges instead of one-shotting.
const MAX_ROUNDS = 3;

// "✨ Ask Grok" — per-stage artistic suggestions for the guided grade workflow,
// now with an iterative refinement loop. Captures the stage's two panes
// (input = through the previous stage, current output = + this stage), sends
// them with the stage's editable param schema to the backend stage-assist
// endpoint, and renders Grok's validated values as apply chips (per param +
// apply-all) with a short rationale.
//
// Refinement: after suggestions are applied the output pane re-renders with the
// new values, so Grok can REVIEW its own result — "🔁 Grok review" runs one
// critique round (fresh capture + round history), and "Auto-refine" runs the
// whole loop hands-free: suggest → apply → wait for the repaint → recapture →
// critique → refine, until Grok returns done:true or the 3-round cap. Every
// applied round is one onChange commit, so ⌘Z steps back round by round.
//
//   stageDef   the current stage (id/name/goal) from gradeStages
//   stages     the workflow's full stage list (STAGES / VIDEO_STAGES) — sent as
//              a pipeline brief so Grok knows what every OTHER sub-stage owns
//              and keeps its moves scoped to this one
//   grade      the grade being edited (current values + applied-state checks)
//   onChange   (nextGrade) => void — commit an edit
//   capture    (n) => frames | Promise<frames> — either the legacy single pair
//              { before, after, reference? } (photo editor) or the multi-sample
//              form { inputFrames: [{label,dataUrl}], outputFrames: [...],
//              referenceFrames?: [...] } (video studio, which seeks the clip to
//              gather n samples; referenceFrames are the paired moments from a
//              saved 🎯 Reference match, aligned by index with the samples)
//   multiFrame the parent can sample several moments across the clip — shows
//              the ×N toggle (video only; photos are inherently one frame)
//   measurements  optional array of objective histogram-measurement lines for
//              the CURRENT stage (measurementLines(stageSugByPath)) — sent so
//              Grok anchors WB/exposure moves to numbers, not JPEG impressions
//   context    { isRed, kind: 'video'|'photo', inputTransform }
//   disabled   block the buttons (e.g. no preview yet)
export default function StageAssist({ stageDef, stages = null, grade, onChange, capture, multiFrame = false, measurements = null, context = {}, disabled = false }) {
  const [busy, setBusy] = useState(false); // a single request in flight
  const [auto, setAuto] = useState(false); // the auto-refine loop is running
  const [err, setErr] = useState('');
  const [note, setNote] = useState(''); // loop status / stop reason
  const [rounds, setRounds] = useState([]); // [{ suggestions, rationale, done, params, frames }]
  // How many moments across the clip to sample per round (surfer/action may
  // only be in frame later in the clip). Default 3 when the parent supports it.
  const [frameN, setFrameN] = useState(multiFrame ? 3 : 1);
  const gradeRef = useRef(grade);
  gradeRef.current = grade;
  // Grade JSON right after OUR last apply — if the current grade differs, the
  // user edited between rounds and a review would critique stale state.
  const expectSigRef = useRef(null);
  const cancelRef = useRef(false);

  // Suggestions/rounds are for one stage's frames — drop them on stage change.
  const stageId = stageDef?.id;
  useEffect(() => {
    setRounds([]); setErr(''); setNote('');
    expectSigRef.current = null;
    cancelRef.current = true; // abandon any loop still awaiting
  }, [stageId]);
  useEffect(() => () => { cancelRef.current = true; }, []);

  const params = stageParamSchema(stageDef, grade);
  if (!API || !params.length) return null;

  // Let the WebGL pane repaint with freshly applied values before recapturing:
  // the LUT recompiles on the grade-prop effect and both studios redraw on a
  // continuous rAF loop, so a short debounce + two frames is comfortably safe.
  const settle = () => new Promise((resolve) => {
    setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(() => resolve())), 700);
  });

  // One request round: capture frames now, send with the round history. The
  // capture may be async (the video studio seeks the clip for extra samples)
  // and returns either the multi-sample arrays or the legacy before/after pair.
  const requestRound = async (history) => {
    const cap = capture ? await Promise.resolve(capture(frameN)) : null;
    let inputFrames = null;
    let outputFrames = null;
    let referenceFrames = null;
    if (cap && Array.isArray(cap.inputFrames) && cap.inputFrames.length && Array.isArray(cap.outputFrames) && cap.outputFrames.length) {
      inputFrames = cap.inputFrames;
      outputFrames = cap.outputFrames;
      if (Array.isArray(cap.referenceFrames) && cap.referenceFrames.length) referenceFrames = cap.referenceFrames.slice(0, 4);
    } else if (cap && cap.before && cap.after) {
      inputFrames = [{ label: '', dataUrl: cap.before }];
      outputFrames = [{ label: '', dataUrl: cap.after }];
      if (cap.reference) referenceFrames = [{ label: 'reference look', dataUrl: cap.reference }];
    }
    if (!inputFrames) {
      throw new Error('Couldn’t capture the stage panes — make sure the split before/after view is showing.');
    }
    // Numeric output-vs-reference deltas (per paired sample) so the model sizes
    // tonal/saturation moves from measurement, not JPEG impressions.
    let refDeltas = null;
    if (referenceFrames) {
      refDeltas = await referenceDeltaLines(outputFrames, referenceFrames).catch(() => null);
    }
    const reqParams = stageParamSchema(stageDef, gradeRef.current);
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;
    const res = await fetch(`${API}/ai/stage-assist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        stage: { id: stageDef.id, name: stageDef.name, goal: stageDef.goal },
        // The whole sub-stage pipeline (goals + owned controls, current one
        // flagged) so Grok stays in this stage's lane and defers everything
        // else to the stage that owns it.
        pipeline: stages ? pipelineBrief(stages, stageDef.id) : undefined,
        params: reqParams,
        // Legacy pair kept alongside the arrays so an older backend still works.
        images: [
          { label: 'stage input', dataUrl: inputFrames[0].dataUrl },
          { label: 'current output', dataUrl: outputFrames[0].dataUrl },
        ],
        inputFrames,
        outputFrames,
        // The paired reference-look frames (🎯 Reference match / the photo
        // editor's match target) — the look Grok should reproduce, this
        // stage's slice of it only.
        ...(referenceFrames ? { referenceFrames } : {}),
        // Measured output-vs-reference deltas for those pairs.
        ...(refDeltas ? { refDeltas } : {}),
        // Objective histogram measurements of the stage input, so the model
        // trusts numbers over its impression of the compressed captures.
        ...(Array.isArray(measurements) && measurements.length ? { measurements } : {}),
        context: {
          isRed: !!context.isRed,
          kind: context.kind || 'video',
          inputTransform: context.inputTransform || 'srgb',
        },
        history,
      }),
    });
    if (!res.ok) {
      let detail = '';
      try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
      throw new Error(detail || `request failed (${res.status})`);
    }
    const data = await res.json();
    return {
      rationale: data.rationale || '',
      suggestions: data.suggestions && typeof data.suggestions === 'object' ? data.suggestions : {},
      done: data.done === true,
      params: reqParams,
      // Clip timestamps of the sampled frames ("0:03 / 0:20"), for the round row.
      frames: outputFrames.map((f) => (f.label || '').split(' / ')[0]).filter(Boolean),
      withRef: !!referenceFrames,
    };
  };

  const historyOf = (list) => list.map((r) => ({ suggestionsApplied: r.suggestions, rationale: r.rationale }));

  // Write a set of suggestion entries into the grade as ONE commit (one undo
  // step per round). Records the expected post-apply signature for staleness.
  const applyEntries = (entries) => {
    let g = clone(gradeRef.current);
    for (const [dotted, value] of entries) {
      const path = parseDottedPath(dotted);
      let o = g;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (o[path[i]] == null || typeof o[path[i]] !== 'object') o[path[i]] = typeof path[i + 1] === 'number' ? [] : {};
        o = o[path[i]];
      }
      o[path[path.length - 1]] = value != null && typeof value === 'object' ? clone(value) : value;
    }
    expectSigRef.current = JSON.stringify(g);
    onChange(g);
    return g;
  };

  // ---- Manual: one fresh ask (round 1) -------------------------------------
  const ask = async () => {
    if (busy || auto) return;
    setErr(''); setNote(''); setRounds([]);
    expectSigRef.current = null;
    setBusy(true);
    try {
      const round = await requestRound([]);
      setRounds([round]);
    } catch (e) {
      setErr(e.message || 'Grok assist failed');
    } finally {
      setBusy(false);
    }
  };

  // ---- Manual: one review round on the applied result ----------------------
  const review = async () => {
    if (busy || auto || !rounds.length) return;
    setErr(''); setNote('');
    setBusy(true);
    try {
      const round = await requestRound(historyOf(rounds));
      setRounds((r) => [...r, round]);
    } catch (e) {
      setErr(e.message || 'Grok review failed');
    } finally {
      setBusy(false);
    }
  };

  // ---- Auto-refine: suggest → apply → repaint → critique → … ---------------
  const autoRefine = async () => {
    if (busy || auto) return;
    setErr(''); setNote(''); setRounds([]);
    expectSigRef.current = null;
    cancelRef.current = false;
    setAuto(true);
    try {
      let done = [];
      let emptyStreak = 0;
      for (let i = 0; i < MAX_ROUNDS; i += 1) {
        const round = await requestRound(historyOf(done));
        if (cancelRef.current) { setNote('Cancelled.'); return; }
        done = [...done, round];
        setRounds(done);
        if (round.done) return; // the round row shows the verdict
        const entries = Object.entries(round.suggestions);
        if (!entries.length) {
          emptyStreak += 1;
          if (emptyStreak >= 2) { setNote('Stopped — no actionable change two rounds in a row.'); return; }
          continue;
        }
        emptyStreak = 0;
        applyEntries(entries);
        if (i === MAX_ROUNDS - 1) { setNote(`Applied — stopped at the ${MAX_ROUNDS}-round cap.`); return; }
        await settle();
        if (cancelRef.current) { setNote('Cancelled — last round is applied (⌘Z to undo).'); return; }
        // Guardrail: if anything else mutated the grade while we waited (a
        // manual slider drag, another tool), reviewing would critique a frame
        // we didn't produce — stop and let the user ask fresh.
        if (JSON.stringify(gradeRef.current) !== expectSigRef.current) {
          setNote('Stopped — the grade changed outside the loop. Ask Grok fresh.');
          return;
        }
      }
    } catch (e) {
      setErr(e.message || 'Auto-refine failed');
    } finally {
      setAuto(false);
    }
  };

  const last = rounds.length ? rounds[rounds.length - 1] : null;
  const entries = last ? Object.entries(last.suggestions) : [];
  const paramFor = (path) => (last?.params || params).find((p) => p.path === path) || null;
  const allApplied = entries.length > 0
    && entries.every(([path, v]) => valueApplied(paramFor(path), getAtPath(gradeRef.current, path), v));

  const applyOne = (dotted, value) => applyEntries([[dotted, value]]);
  const applyAll = () => { if (entries.length) applyEntries(entries); };

  // Review is only meaningful on OUR OWN applied result: something was applied
  // (expectSig set) and the user hasn't edited since (sig still matches).
  const gradeSig = JSON.stringify(grade);
  const reviewable = rounds.length > 0 && rounds.length < MAX_ROUNDS && !last?.done
    && expectSigRef.current != null && expectSigRef.current === gradeSig;

  // Compact per-round summary line ("applied 2", "nudged Log exposure +0.1EV",
  // "✓ satisfied") — the rationale expands underneath.
  const roundHead = (r, i) => {
    if (r.done) return '✓ satisfied';
    const es = Object.entries(r.suggestions);
    if (!es.length) return 'no change proposed';
    if (es.length === 1) {
      const p = (r.params || []).find((x) => x.path === es[0][0]);
      return `${i === 0 ? 'suggested' : 'nudged'} ${p?.label || es[0][0]} ${describeValue(p, es[0][1])}`;
    }
    return `${i === 0 ? 'suggested' : 'refined'} ${es.length} params`;
  };
  // Which clip moments a round's frames were sampled at — only worth showing
  // when more than one frame went out. A 🎯 marks rounds graded against the
  // saved reference look.
  const frameTag = (r) => {
    const spots = Array.isArray(r?.frames) && r.frames.length > 1 ? r.frames.join(' · ') : '';
    if (r?.withRef) return spots ? `${spots} · 🎯 vs reference` : '🎯 vs reference';
    return spots;
  };

  return (
    <div className="cs-grok">
      <div className="cs-grok-row">
        <button
          type="button"
          className="cs-btn cs-btn-ghost cs-btn-sm"
          disabled={busy || auto || disabled}
          title="One round: send this stage's before/after frames to Grok — it suggests values for just this stage's controls, with a short artistic rationale"
          onClick={ask}
        >
          {busy && !auto ? <><span className="cs-spinner" /> Asking Grok…</> : '✨ Ask Grok'}
        </button>
        {multiFrame && (
          <button
            type="button"
            className={`cs-grok-nf${frameN > 1 ? ' on' : ''}`}
            disabled={busy || auto || disabled}
            title={frameN > 1
              ? 'Sampling 3 moments across the clip (playhead + spread) so Grok sees frames where the surfer/action is actually visible — click for the single playhead frame'
              : 'Single playhead frame — click to sample 3 moments across the clip (the subject may only appear later)'}
            onClick={() => setFrameN((k) => (k > 1 ? 1 : 3))}
          >
            ×{frameN}
          </button>
        )}
        <button
          type="button"
          className="cs-btn cs-btn-ghost cs-btn-sm"
          disabled={busy || auto || disabled}
          title={`Hands-free loop: Grok suggests → applies → looks at the re-rendered result → refines, until it's satisfied (max ${MAX_ROUNDS} rounds). Each round is one undo step.`}
          onClick={autoRefine}
        >
          {auto ? <><span className="cs-spinner" /> Refining… round {rounds.length + 1}/{MAX_ROUNDS}</> : '⟳ Auto-refine'}
        </button>
        {auto && (
          <button
            type="button"
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => { cancelRef.current = true; }}
            title="Stop the loop after the current round (applied rounds stay — ⌘Z steps back)"
          >
            ■ Stop
          </button>
        )}
        {!auto && entries.length > 0 && (
          <button
            type="button"
            className="cs-btn cs-btn-primary cs-btn-sm"
            disabled={allApplied}
            onClick={applyAll}
            title="Apply every Grok suggestion from the latest round in one edit (⌘Z undoes)"
          >
            {allApplied ? '✓ Grok applied' : `Apply all Grok (${entries.length})`}
          </button>
        )}
        {!auto && rounds.length > 0 && (
          <button
            type="button"
            className="cs-btn cs-btn-ghost cs-btn-sm"
            disabled={busy || disabled || !reviewable}
            title={reviewable
              ? 'Have Grok look at the re-rendered result of its applied suggestions and either refine them or call it done'
              : rounds.length >= MAX_ROUNDS
                ? `Round cap reached (${MAX_ROUNDS}) — ask fresh to keep going`
                : last?.done
                  ? 'Grok is satisfied with this stage'
                  : expectSigRef.current == null
                    ? 'Apply some Grok suggestions first, then have Grok review the result'
                    : 'The grade changed since Grok\'s last apply — ask Grok fresh instead'}
            onClick={review}
          >
            {busy && !auto ? <><span className="cs-spinner" /> Reviewing…</> : '🔁 Grok review'}
          </button>
        )}
        {!auto && rounds.length > 0 && (
          <button type="button" className="cs-grok-dismiss" onClick={() => { setRounds([]); setNote(''); expectSigRef.current = null; }} title="Dismiss Grok's suggestions">✕</button>
        )}
      </div>
      {err && <div className="cs-grok-err">{err}</div>}
      {note && <div className="cs-grok-note">{note}</div>}
      {rounds.length > 1 && (
        <div className="cs-grok-rounds">
          {rounds.slice(0, -1).map((r, i) => (
            <details key={i} className="cs-grok-round">
              <summary>Round {i + 1} · {roundHead(r, i)}</summary>
              {frameTag(r) && <div className="cs-grok-frames">frames @ {frameTag(r)}</div>}
              {r.rationale && <div className="cs-grok-why">{r.rationale}</div>}
            </details>
          ))}
        </div>
      )}
      {last && (
        <>
          {rounds.length > 1 && (
            <div className="cs-grok-roundtag">Round {rounds.length} · {roundHead(last, rounds.length - 1)}</div>
          )}
          {frameTag(last) && <div className="cs-grok-frames">frames @ {frameTag(last)}</div>}
          {entries.length > 0 && (
            <div className="cs-grok-chips">
              {entries.map(([path, value]) => {
                const p = paramFor(path);
                const applied = valueApplied(p, getAtPath(gradeRef.current, path), value);
                return (
                  <span key={path} className="cs-grok-chip" title={p?.hint || ''}>
                    <i>Grok:</i> {p?.label || path} <b>{describeValue(p, value)}</b>
                    <button type="button" disabled={applied || auto} onClick={() => applyOne(path, value)}>
                      {applied ? '✓' : 'Apply'}
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {last.rationale && <div className={`cs-grok-why${last.done ? ' is-done' : ''}`}>{last.done ? '✓ ' : ''}{last.rationale}</div>}
          {!entries.length && !last.rationale && <div className="cs-grok-why">Grok had no changes to suggest for this stage.</div>}
        </>
      )}
    </div>
  );
}
